import { bool2str, componentId, KeyCode, revealElements, setAttributes } from '../../util';
import { MultiPanelElement } from './multi-panel-element';

const ACCORDION_CLASS_TITLE = 'js-accordion__title';
const ACCORDION_CLASS_CONTENT = 'js-accordion__content';
const ACCORDION_CLASS_BUTTON = '__button';

const getPanelId = r => `accordion${r}_panel`;
const getButtonId = r => `accordion${r}_button`;

/**
 * Takes a structure like
 * ```html
 * div.js-accordion
 *   h2.js-accordion__title(data-selected="true")
 *   div.js-accordion__content
 *     //- arbitrary content
 *   h2.js-accordion__title
 *   div.js-accordion__content
 * ```
 * and produces
 * ```html
 * div.js-accordion
 *   button.<baseclass>__button
 *   div.js-accordion__content
 *     h2.js-accordion__title(data-selected="true")
 *     //- arbitrary content
 *   button.<baseclass>__button
 *   div.js-accordion__content
 *     h2.js-accordion__title
 * ```
 *
 * ## API
 * const a = new Accordion(document.querySelector('.js-accordion'), 'accordion')
 * a.activePanel // div.js-accordion__content || null
 * a.activePanel = document.querySelectorAll('.js-accordion__content')[1]
 * // or
 * a.openPanel(document.querySelectorAll('.js-accordion__content')[1], true|false)
 * a.multiselect // div.js-accordion[data-accordion-multiselect=true]
 * a.destroy() // revert changes made by this component
 *
 * ## Keyboard-interaction
 * on buttons
 * * UP / LEFT: focus previous button
 * * DOWN / RIGHT: focus next button
 * * HOME: focus first button
 * * END: focus last button
 * on panel content
 * * Ctrl + UP: focus current button
 * * Ctrl + PAGE_UP: focus previous button
 * * Ctrl + PAGE_DOWN: focus next button
 * the last two will most likely not work as intended for most browsers
 */
class Accordion extends MultiPanelElement {

    /**
     * @param {HTMLDivElement} element
     * @param {string} baseclass
     */
    constructor(element, baseclass) {
        super(element);
        this.destroy = this.destroy.bind(this);
        this.openPanel = this.openPanel.bind(this);
        this._construct = this._construct.bind(this);
        this._openPanel = this._openPanel.bind(this);
        this._onButtonClick = this._onButtonClick.bind(this);
        this._onButtonKey = this._onButtonKey.bind(this);
        this._onPanelKey = this._onPanelKey.bind(this);
        /**
         * @type {HTMLDivElement}
         * @private
         */
        this._element = element;
        this._baseclass = baseclass;
        this._buttons = [];
        this._isClosable = true;
        this._construct();
    }

    get multiselect() {
        return this._element.dataset.accordionMultiselect === 'true';
    }

    set multiselect(value) {
        if (value === true) {
            this._element.dataset.accordionMultiselect = 'true';
        } else {
            delete this._element.dataset.accordionMultiselect;
        }
    }

    get panels() {
        return Array.from(this._element.querySelectorAll('[role=region]'));
    }

    get activePanel() {
        return this._element.querySelector('[role=region][aria-hidden=false]');
    }

    set activePanel(panel) {
        this.openPanel(panel, true);
    }

    openPanel(panel, targetState) {
        if (!this._element.contains(panel)) {
            throw new ReferenceError('Given element is not part of this Accordion');
        }
        this._openPanel(panel, targetState);
    }

    /**
     * Create DOM structure and attach attributes and events
     * @private
     */
    _construct() {
        let tabbingEnabled = false;
        Array.from(this._element.querySelectorAll('.' + ACCORDION_CLASS_TITLE)).forEach((title, i) => {
            let panel = title.nextElementSibling;
            const isPanel = p => p.classList && p.classList.contains(ACCORDION_CLASS_CONTENT);
            while (!isPanel(panel)) {
                panel = panel.nextElementSibling;
            }
            if (panel === null || !isPanel(panel)) {
                throw new ReferenceError(`Accordion element ${i + 1} has no adjacent content`);
            }
            const isSelected = title.dataset.selected === 'true';
            const randId = componentId();
            const panelId = getPanelId(randId);
            const buttonId = getButtonId(randId);
            const button = document.createElement('BUTTON');
            this._buttons.push(button);
            button.innerHTML = (title.firstChild || title).innerHTML;
            button.classList.add(this._baseclass + ACCORDION_CLASS_BUTTON);
            button.addEventListener('click', this._onButtonClick);
            button.addEventListener('keydown', this._onButtonKey);
            setAttributes(button, {
                'aria-controls': panelId,
                'aria-expanded': isSelected,
                'id': buttonId,
                'tabindex': '0',
                'type': 'button',
            });
            panel.parentElement.insertBefore(button, title);
            // move title into panel
            panel.insertBefore(title, panel.firstChild);
            setAttributes(panel, {
                'aria-labelledby': buttonId,
                'aria-hidden': !isSelected,
                'role': 'region',
                'id': panelId,
            });
            panel.addEventListener('keydown', this._onPanelKey);
            if (!tabbingEnabled && isSelected) {
                button.removeAttribute('tabindex');
                button.dataset.selected = 'true';
                tabbingEnabled = true;
            }
        });
        if (!tabbingEnabled && this._buttons.length > 0) {
            this._buttons[0].removeAttribute('tabindex');
            if (!this._isClosable) {
                this._buttons[0].dataset.selected = 'true';
                this._buttons[0].nextElementSibling.setAttribute('aria-hidden','false');
            }
        }
        this._onHashChange();
    }

    _openPanel(panel, targetState) {
        const activePanel = this.activePanel;
        if (panel === activePanel && targetState) {
            return;
        }
        if (!this.multiselect && targetState) {
            Array.from(this._element.querySelectorAll('[role=region][aria-hidden=false]'))
                .forEach(e => this._openPanel(e, false));
        }
        const button = panel.previousElementSibling;
        setAttributes(button, {
            'aria-expanded': bool2str(targetState),
            'data-selected': bool2str(targetState),
        });
        const title = panel.firstElementChild;
        title.setAttribute('data-selected', bool2str(targetState));
        panel.setAttribute('aria-hidden', bool2str(!targetState));
        if (targetState) {
            revealElements(panel, true);
        }
    }

    _focusButton(currentButton, nextIndex) {
        setAttributes(currentButton, {
            'tabindex': '0',
        });
        const nextButton = this._buttons[nextIndex];
        setAttributes(nextButton, {
            'tabindex': null,
        });
        nextButton.focus();
    }

    _focusNextButton(currentIndex) {
        this._focusButton(
            this._buttons[currentIndex],
            (currentIndex + 1) % this._buttons.length
        );
    }

    _focusPreviousButton(currentIndex) {
        this._focusButton(
            this._buttons[currentIndex],
            (currentIndex - 1 + this._buttons.length) % this._buttons.length
        );
    }

    /**
     * @param {MouseEvent} e
     * @private
     */
    _onButtonClick(e) {
        e.preventDefault();
        const button = e.currentTarget;
        const panel = this._element.querySelector('#' + button.getAttribute('aria-controls'));
        setTimeout(() => button.focus(), 0);
        this._openPanel(panel, button.getAttribute('aria-expanded') !== 'true');
    }

    /**
     * @param {KeyboardEvent} e
     * @private
     */
    _onPanelKey(e) {
        let panel = e.target;
        do {
            panel = panel.parentElement;
        } while (panel.parentElement !== this._element);
        if (e.ctrlKey) {
            // jump into tab buttons
            if (e.keyCode === KeyCode.UP) {
                e.preventDefault();
                this._buttons.filter(e => !e.hasAttribute('tabindex'))[0].focus();
                return;
            }
            const button = panel.previousElementSibling;
            let currentTabIndex = this._buttons.indexOf(button);
            // jump to previous tab button
            if (e.keyCode === KeyCode.PAGE_UP) {
                e.preventDefault();
                this._focusPreviousButton(currentTabIndex);
            }
            // jump to next tab button
            if (e.keyCode === KeyCode.PAGE_DOWN) {
                e.preventDefault();
                this._focusNextButton(currentTabIndex);
            }
        }
    }

    /**
     * @param {KeyboardEvent} e
     * @private
     */
    _onButtonKey(e) {
        const button = e.target;
        let currentTabIndex = this._buttons.indexOf(button);
        const buttonCount = this._buttons.length;
        if (!e.ctrlKey) {
            switch (e.keyCode) {
                // previous tab
                case KeyCode.LEFT:
                case KeyCode.UP:
                    e.preventDefault();
                    this._focusPreviousButton(currentTabIndex);
                    break;
                // next tab
                case KeyCode.RIGHT:
                case KeyCode.DOWN:
                    e.preventDefault();
                    this._focusNextButton(currentTabIndex);
                    break;
                // first tab
                case KeyCode.HOME:
                    e.preventDefault();
                    this._focusButton(button, 0);
                    break;
                // last tab
                case KeyCode.END:
                    e.preventDefault();
                    this._focusButton(button, buttonCount - 1);
                    break;
            }
        }
    }

    destroy() {
        super.destroy();
        this._buttons.forEach(button => {
            const panel = button.nextElementSibling;
            const title = panel.firstElementChild;
            panel.parentElement.insertBefore(title, panel);
            setAttributes(panel, {
                'aria-labelledby': null,
                'aria-hidden': null,
                'role': null,
                'id': null,
            });
            panel.removeEventListener('keypress', this._onPanelKey);
            button.remove();
        });
    }
}

export {
    Accordion,
};
